import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. A soap film on black; any wash would put a
// floor under the black spots, which are what make the colours burst.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'bubble',
  family: 'organic',
  danceability: 0.62,
  requiresTier: 1,
  requiresFloatTextures: true,
  energyLevel: 0.58,
  bpmMin: 60,
  bpmMax: 135,
  movement: 0.65,
  tonality: 0.85,
  enabled: FLOAT_TEXTURES_OK
};

// Bubble: the surface of a soap film seen in macro, full frame, swirling and
// saturated, on black. Psychedelic register.
//
// THE MODEL IS A THICKNESS FIELD in nanometres on a float grid, as before -
// the colour of a soap film is the interference between its two faces and
// depends only on its thickness - but the film is no longer draining on a
// ring: it is TURBULENT. The grid carries (h, vx, vy, omega). The velocity
// is advected with the film and pulled toward an analytic field of six to
// ten drifting vortices plus a small-scale curl and a faint gravity, and a
// vorticity confinement force pushes the swirls back where numerical
// diffusion smears them, so the volutes keep filaments a few texels wide.
// The thickness is advected bicubically (Catmull-Rom, sixteen taps) at
// tier 2 for the same reason.
//
// THE COLOUR is physical: twelve wavelengths from 400 to 700 nm, the
// two-beam reflectance sin^2(2 pi n h / lambda), integrated against the
// CIE matching functions (Wyman-Sloan-Shirley fit) and converted to linear
// sRGB. Three channels gave false, washed colours; so did thicknesses past
// 700 nm, where the orders blend to a pinkish grey. The field is kept in
// 0..700 nm, mostly 150..550 where magenta, deep blue, gold and green are
// at their most saturated, and under 40 nm it is black. The whole law is
// precomputed into a 1024-entry lookup table, so the full-screen pass costs
// four taps and one lookup. The palette never touches it.
//
// THE PASSES run inside the display quad's onBeforeRender, the one place a
// party-mode visualizer may touch the renderer (see echo.js / advect.js).

const H_MAX = 700;
const N_REF = 1.33;
const BLACK_NM = 40;             // under this, black
// 0.92, not the brief's 2.2: the law is normalised so that a flat spectrum is
// white (1, 1, 1), hence the vivid orders already peak at 1.0-1.15 per
// channel; 2.2 clipped the whole film to white (captures at mean 249/255).
// The brief's 2.2 is relative to the few-percent physical reflectance, which
// the normalisation has already removed.
// 0.55: at 0.72 the composer's bloom and ACES turned wide areas into a
// pastel pink-white wash (measured 159/255 mean after a few drops).
const REFLECT_GAIN = 0.55;
const CONTRAST = 1.5;              // dense: the composer's ACES lifts the mids
const H_REST = 300;              // the slow relaxation's target, mid vivid range
const RELAX_PER_S = 0.03;
const SOURCE_NM_PER_S = 10;      // a slow, spatially varying thickening / thinning: against the relaxation, the field keeps its contrast
const EAT_NM_PER_S = 110;        // how fast a black spot eats its border
const FIXED_DT = 1 / 30;
const MAX_SUBSTEPS = 2;
const LUT_SIZE = 1024;
const VORTEX_MAX = 10;
const DROP_MAX = 4;
const DROP_INJECT_S = 0.3;
const SPOT_GROW_BEATS = 4;
const SPOT_FILL_BEATS = 2;
const SPOT_RADIUS = 0.22;        // in aspect units (height = 1)
// +230 with the field capped at 600: the sweep gold -> magenta -> blue per
// pixel, keeping the structure; +450 clamped the whole film to one flat green.
const DROP_ADD_NM = 230;
const DROP_RELEASE_BEATS = 6;    // the thickening is given back over six beats: no accumulation drop after drop
const H_CAP = 560;
const DROP_FRAG_BEATS = 2;
const VORTEX_DRIFT = 0.02;       // uv per second
const BAR_BOOST = 2.5;
const SAFE_FLOOR = 0.55;
const BLACK_LEVEL = 0.02;
const GRAIN = 0.02;
const DROP_SCORE_MIN = 0.7;
const DROP_STALE_MS = 250;
const FREE_BEAT_S = 0.5;
const PROBE_W = 128, PROBE_H = 72;

export const LAMBDAS = [400, 427, 455, 482, 509, 536, 564, 591, 618, 645, 673, 700];

const TIER = {
  // Tier 0 is a live-downgrade refuge only (requiresTier 1).
  0: { width: 256, cubic: false, vortices: 6 },
  1: { width: 384, cubic: false, vortices: 8 },
  2: { width: 512, cubic: true, vortices: 10 }
};

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smoother(u) { u = clamp01(u); return u * u * u * (u * (u * 6 - 15) + 10); }

// --- the law, in JavaScript: the table is built from it, the tests read it
// CIE 1931 matching functions, the multi-lobe Gaussian fit of Wyman, Sloan
// and Shirley (2013).
function lobe(l, mu, s1, s2) { const s = l < mu ? s1 : s2; const t = (l - mu) / s; return Math.exp(-0.5 * t * t); }
export function cieXYZ(l) {
  return [
    1.056 * lobe(l, 599.8, 37.9, 31.0) + 0.362 * lobe(l, 442.0, 16.0, 26.7) - 0.065 * lobe(l, 501.1, 20.4, 26.2),
    0.821 * lobe(l, 568.8, 46.9, 40.5) + 0.286 * lobe(l, 530.9, 16.3, 31.1),
    1.217 * lobe(l, 437.0, 11.8, 36.0) + 0.681 * lobe(l, 459.0, 26.0, 13.8)
  ];
}
function xyzToRgb(X, Y, Z) {
  return [3.2406 * X - 1.5372 * Y - 0.4986 * Z, -0.9689 * X + 1.8758 * Y + 0.0415 * Z, 0.0557 * X - 0.2040 * Y + 1.0570 * Z];
}
// White balance: a flat spectrum (every wavelength reflected alike) must come
// out neutral, so each channel is divided by what the flat spectrum gives.
const WHITE = (function () {
  let X = 0, Y = 0, Z = 0;
  for (const l of LAMBDAS) { const c = cieXYZ(l); X += c[0]; Y += c[1]; Z += c[2]; }
  return xyzToRgb(X, Y, Z);
})();

// Linear RGB of a film of thickness h (nm), before the display's gain: the
// two-beam reflectance per wavelength (the half-wave shift of the denser
// face folded in: h -> 0 is dark), integrated against the CIE functions.
export function couleurFilm(h) {
  let X = 0, Y = 0, Z = 0;
  for (const l of LAMBDAS) {
    const s = Math.sin(2 * Math.PI * N_REF * h / l);
    const R = s * s;
    const c = cieXYZ(l);
    X += R * c[0]; Y += R * c[1]; Z += R * c[2];
  }
  const rgb = xyzToRgb(X, Y, Z);
  return rgb.map((v, k) => Math.max(0, v / WHITE[k]));
}
// What the screen gets: gain, a light contrast curve, black under 40 nm.
export function couleurAffichee(h) {
  const noir = clamp01((h - BLACK_NM * 0.6) / (BLACK_NM * 0.9));
  // The first order (80-140 nm) reflects every wavelength alike: a silvery
  // white, physically right and the brightest thing in the whole range. It
  // is held down to a dim straw, or the thin edge of every black spot and
  // every thin region reads as a white sheet.
  const premier = 0.35 + 0.65 * clamp01((h - 90) / 90);
  return couleurFilm(h).map((v) => Math.min(1, Math.pow(Math.min(1, v * REFLECT_GAIN * premier), CONTRAST)) * noir * noir);
}
export function construireLut() {
  const data = new Uint8Array(LUT_SIZE * 4);
  for (let i = 0; i < LUT_SIZE; i++) {
    const c = couleurAffichee(i / (LUT_SIZE - 1) * H_MAX);
    data[i * 4] = Math.round(c[0] * 255); data[i * 4 + 1] = Math.round(c[1] * 255); data[i * 4 + 2] = Math.round(c[2] * 255); data[i * 4 + 3] = 255;
  }
  return data;
}

// ============================================================================
// Shaders
// ============================================================================
const NOISE_CHUNK = `
  float hash21(vec2 p) { p = fract(p * vec2(0.1031, 0.1030)); p += dot(p, p.yx + 33.33); return fract((p.x + p.y) * p.x); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    i = mod(i, 128.0);
    float a = hash21(i), b = hash21(i + vec2(1.0, 0.0)), c = hash21(i + vec2(0.0, 1.0)), d = hash21(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  float psi(vec2 p) { return 0.65 * vnoise(p) + 0.35 * vnoise(p * 2.03 + 7.1); }
`;
// Manual filtering: the Pi samples float textures NEAREST only.
const SAMPLING_CHUNK = `
  vec4 texBilinear(sampler2D tex, vec2 uv, vec2 res) {
    vec2 st = uv * res - 0.5;
    vec2 fl = floor(st);
    vec2 fr = st - fl;
    vec2 base = (fl + 0.5) / res;
    vec2 d = 1.0 / res;
    vec4 a = texture2D(tex, base);
    vec4 b = texture2D(tex, base + vec2(d.x, 0.0));
    vec4 c = texture2D(tex, base + vec2(0.0, d.y));
    vec4 e = texture2D(tex, base + d);
    return mix(mix(a, b, fr.x), mix(c, e, fr.x), fr.y);
  }
  // Catmull-Rom on the R channel: sixteen taps, no smearing of a filament.
  float cubicW0(float t) { return ((-0.5 * t + 1.0) * t - 0.5) * t; }
  float cubicW1(float t) { return (1.5 * t - 2.5) * t * t + 1.0; }
  float cubicW2(float t) { return ((-1.5 * t + 2.0) * t + 0.5) * t; }
  float cubicW3(float t) { return (0.5 * t - 0.5) * t * t; }
  float texCubicR(sampler2D tex, vec2 uv, vec2 res) {
    vec2 st = uv * res - 0.5;
    vec2 fl = floor(st);
    vec2 fr = st - fl;
    vec2 d = 1.0 / res;
    float wx0 = cubicW0(fr.x), wx1 = cubicW1(fr.x), wx2 = cubicW2(fr.x), wx3 = cubicW3(fr.x);
    float wy[4];
    wy[0] = cubicW0(fr.y); wy[1] = cubicW1(fr.y); wy[2] = cubicW2(fr.y); wy[3] = cubicW3(fr.y);
    float sum = 0.0;
    for (int j = 0; j < 4; j++) {
      vec2 row = (fl + vec2(-0.5, float(j) - 0.5)) / res;
      float r = wx0 * texture2D(tex, row).r + wx1 * texture2D(tex, row + vec2(d.x, 0.0)).r
              + wx2 * texture2D(tex, row + vec2(2.0 * d.x, 0.0)).r + wx3 * texture2D(tex, row + vec2(3.0 * d.x, 0.0)).r;
      sum += wy[j] * r;
    }
    return sum;
  }
`;
const fullscreenVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// The simulation step. R = thickness (nm), G B = velocity (uv per second),
// A = vorticity. Coordinates for anything round are "aspect units": x
// scaled by the aspect so the height is 1 and a vortex is a circle.
const simFragmentShader = `
  precision highp float;
  float sq(float x) { return x * x; }   // pow() of a negative base is undefined in GLSL
  uniform sampler2D uPrev;
  uniform vec2 uRes;
  uniform float uAspect;
  uniform float uDt;
  uniform float uTime;
  uniform float uCubic;
  uniform vec4 uVort[${VORTEX_MAX}];     // x, y (aspect units), gamma, radius
  uniform float uNVort;
  uniform float uVortGain;
  uniform float uCurl;
  uniform float uConf;
  uniform float uGravity;
  uniform vec4 uDrops[${DROP_MAX}];      // x, y, h target, age (< 0: none)
  uniform float uDropR;
  uniform vec4 uSpot;                    // x, y, radius, stage (0 none, 1 black growing, 2 filling)
  uniform vec3 uFill;                    // direction x, y, progress 0..1
  uniform float uAddStep;                // the DROP's thickening, nm this step
  uniform float uReset;
  uniform float uSeed;
  varying vec2 vUv;
  ${NOISE_CHUNK}
  ${SAMPLING_CHUNK}
  vec2 toA(vec2 uv) { return vec2(uv.x * uAspect, uv.y); }
  float initial(vec2 uv) {
    vec2 p = toA(uv) * 3.0 + uSeed;
    return ${H_REST.toFixed(1)} + 170.0 * (psi(p) - 0.5) * 2.0 + 90.0 * (psi(p * 2.7 + 3.0) - 0.5) * 2.0 + 40.0 * (vnoise(p * 9.0 + 5.0) - 0.5);
  }
  // The analytic field: drifting Lamb-Oseen vortices, a small-scale curl,
  // and a faint gravity. In aspect units per second, converted to uv.
  vec2 analytic(vec2 uv) {
    vec2 p = toA(uv);
    vec2 v = vec2(0.0);
    for (int i = 0; i < ${VORTEX_MAX}; i++) {
      if (float(i) >= uNVort) break;
      vec4 w = uVort[i];
      vec2 d = p - w.xy;
      float r2 = dot(d, d);
      float R2 = w.w * w.w;
      v += uVortGain * w.z * vec2(-d.y, d.x) / (6.2831853 * (r2 + 0.02 * R2)) * (1.0 - exp(-r2 / R2));
    }
    float t = uTime * 0.04;
    vec2 q = p * 6.0 + vec2(t, -t * 0.6);
    float e = 0.02;
    float c = psi(q);
    v += uCurl * vec2(psi(q + vec2(0.0, e)) - c, -(psi(q + vec2(e, 0.0)) - c)) / e;
    v.y -= uGravity;
    return vec2(v.x / uAspect, v.y);
  }
  void main() {
    vec2 uv = vUv;
    vec2 d = 1.0 / uRes;
    vec4 c = texture2D(uPrev, uv);
    vec4 l = texture2D(uPrev, uv - vec2(d.x, 0.0));
    vec4 r = texture2D(uPrev, uv + vec2(d.x, 0.0));
    vec4 b = texture2D(uPrev, uv - vec2(0.0, d.y));
    vec4 t = texture2D(uPrev, uv + vec2(0.0, d.y));
    // Vorticity here, from the neighbours' velocities (uv/s over uv).
    float w = (r.z - l.z) / (2.0 * d.x) - (t.y - b.y) / (2.0 * d.y);
    // Confinement: push toward the ridge of |omega|, scaled by the texel.
    vec2 eta = vec2(abs(r.a) - abs(l.a), abs(t.a) - abs(b.a));
    vec2 N = eta / (length(eta) + 1e-4);
    vec2 conf = uConf * vec2(N.y * w, -N.x * w) * d.x;
    // Semi-Lagrangian: fetch where this parcel came from.
    vec2 v = c.yz;
    vec2 src = uv - v * uDt;
    src = clamp(src, 0.5 * d, 1.0 - 0.5 * d);
    vec4 back = texBilinear(uPrev, src, uRes);
    float h = uCubic > 0.5 ? texCubicR(uPrev, src, uRes) : back.r;
    // The velocity: carried with the film, pulled to the analytic field,
    // sharpened by the confinement.
    vec2 vNew = mix(back.yz, analytic(uv), 0.08) + conf * uDt;
    float sp = length(vNew);
    if (sp > 0.6) vNew *= 0.6 / sp;
    if (uReset > 0.5) { h = initial(uv); vNew = analytic(uv); w = 0.0; }
    // Slow relaxation toward the middle of the vivid range - except black,
    // which stays black until something fills it - and the black spots
    // eating their border.
    if (h > 60.0) h += (${H_REST.toFixed(1)} - h) * ${RELAX_PER_S.toFixed(3)} * uDt;
    // The slow source: a drifting pattern of thickening and thinning, so the
    // relaxation never flattens the field into one colour.
    vec2 pa = toA(uv);
    if (h > 60.0) h += ${SOURCE_NM_PER_S.toFixed(1)} * (psi(pa * 2.2 + vec2(uTime * 0.02, -uTime * 0.015)) - 0.5) * 2.0 * uDt;
    float nb = min(min(l.r, r.r), min(b.r, t.r));
    if (h > ${BLACK_NM.toFixed(1)} && nb < ${BLACK_NM.toFixed(1)}) h -= ${EAT_NM_PER_S.toFixed(1)} * uDt;
    // The beat's drops: a cap of another thickness, set over a third of a
    // second, which the field then carries.
    vec2 p = toA(uv);
    for (int i = 0; i < ${DROP_MAX}; i++) {
      vec4 dr = uDrops[i];
      if (dr.w < 0.0 || dr.w > ${DROP_INJECT_S.toFixed(2)}) continue;
      float rr = length(p - dr.xy) / uDropR;
      float g = 1.0 - smoothstep(0.0, 1.0, rr);
      h = mix(h, dr.z, g * min(1.0, uDt * 14.0));
    }
    // The phrase's black spot, then the thick flow that fills it.
    if (uSpot.w > 0.5) {
      vec2 ds = p - uSpot.xy;
      float rs = length(ds);
      if (uSpot.w < 1.5) {
        float g = 1.0 - smoothstep(uSpot.z * 0.7, uSpot.z, rs);
        h = mix(h, 0.0, g * min(1.0, uDt * 4.0));
      } else {
        float along = dot(ds, uFill.xy) / uSpot.z;          // -1 .. 1 across the spot
        float front = -1.3 + 2.6 * uFill.z;
        float g = (1.0 - smoothstep(uSpot.z * 0.9, uSpot.z * 1.1, rs)) * (1.0 - smoothstep(front - 0.25, front, along));
        h = mix(h, 470.0 + 60.0 * sin(along * 9.0), g * min(1.0, uDt * 9.0));
      }
    }
    h += uAddStep;
    h = clamp(h, 0.0, ${H_CAP.toFixed(1)});
    gl_FragColor = vec4(h, vNew, w);
  }
`;

// The display: four taps, one lookup, a gliding window, grain.
const displayFragmentShader = `
  precision highp float;
  uniform sampler2D uH;
  uniform sampler2D uLut;
  uniform vec2 uRes;
  uniform float uAspect;
  uniform float uShowH;
  uniform float uCubic;
  uniform float uTime;
  uniform float uSeed;
  uniform vec2 uWinPos;
  uniform vec3 uWinTint;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  ${NOISE_CHUNK}
  ${SAMPLING_CHUNK}
  float safeAt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  void main() {
    float h = texBilinear(uH, vUv, uRes).r;
    vec3 col = texture2D(uLut, vec2(clamp(h, 0.0, ${H_MAX.toFixed(1)}) / ${H_MAX.toFixed(1)}, 0.5)).rgb;
    // The surface: the thickness gradient tilts a normal (two more bilinear
    // taps, no per-pixel noise - that cost the Pi 20 ms), which reflects a
    // blurred window that glides as the film flows.
    vec2 e = 2.0 / uRes;
    float hx = texBilinear(uH, vUv + vec2(e.x, 0.0), uRes).r - h;
    float hy = texBilinear(uH, vUv + vec2(0.0, e.y), uRes).r - h;
    vec3 N = normalize(vec3(-hx * 0.004, -hy * 0.004, 1.0));
    vec3 R = reflect(vec3(0.0, 0.0, -1.0), N);
    vec2 w = R.xy * 3.0 - uWinPos;
    float box = (1.0 - smoothstep(0.25, 0.6, abs(w.x))) * (1.0 - smoothstep(0.35, 0.75, abs(w.y)));
    float bars = 0.75 + 0.25 * (step(0.06, abs(w.x)) * step(0.06, abs(w.y)));
    float present = smoothstep(${BLACK_NM.toFixed(1)} * 0.8, ${BLACK_NM.toFixed(1)} * 1.6, h);
    col += uWinTint * box * bars * 0.14 * present;
    if (uShowH > 0.5) col = vec3(h / ${H_MAX.toFixed(1)});
    col = max(col, vec3(${BLACK_LEVEL.toFixed(2)}));
    col *= 1.0 + ${GRAIN.toFixed(2)} * 2.0 * (hash21(floor(gl_FragCoord.xy) + uSeed) - 0.5);
    gl_FragColor = vec4(col * safeAt(vUv), 1.0);
  }
`;

// The readback probe: h sampled at PROBE_W x PROBE_H.
const probeFragmentShader = `
  precision highp float;
  uniform sampler2D uH;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uH, vUv).r, 0.0, 0.0, 1.0); }
`;

const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

// The materials, built once per page and never disposed: the Pi recompiles
// a disposed program at every appearance (seconds). A cached material keeps
// ITS uniforms object; init() rewrites the values, never the object.
const MATERIAUX = new Map();
function materiau(cle, fabrique) {
  let m = MATERIAUX.get(cle);
  if (!m) { m = fabrique(); MATERIAUX.set(cle, m); }
  return m;
}
let LUT_TEXTURE = null;
function lutTexture() {
  if (!LUT_TEXTURE) {
    LUT_TEXTURE = new THREE.DataTexture(construireLut(), LUT_SIZE, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    LUT_TEXTURE.minFilter = THREE.LinearFilter; LUT_TEXTURE.magFilter = THREE.LinearFilter;
    LUT_TEXTURE.wrapS = THREE.ClampToEdgeWrapping; LUT_TEXTURE.wrapT = THREE.ClampToEdgeWrapping;
    LUT_TEXTURE.needsUpdate = true;
  }
  return LUT_TEXTURE;
}

// ============================================================================
export class BubbleVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    this._showH = urlParam('bubbleh') === '1';
    this._debugBurst = urlParam('bubbleburst') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const aspect = camera.aspect > 0 ? camera.aspect : 16 / 9;
    this._aspect = aspect;
    const W = this._layout.width, H = Math.max(64, Math.round(W / aspect));
    this._res = new THREE.Vector2(W, H);
    this._time = 0; this._paused = false; this._accumulator = 0;
    this._beatS = FREE_BEAT_S; this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null;
    this._freeBeatAt = 0; this._beatCount = 0; this._beatsSincePhrase = 0;
    this._low = 0.5; this._mid = 0.5; this._high = 0.5;
    this._drops = []; this._dropIdx = 0; this._marksRef = undefined; this._lastNowMs = null; this._debugBurstAt = 4;
    this._lastPalette = null;
    this._objects = []; this._geometries = [];
    this._winStep = 0; this._winFrom = new THREE.Vector2(-0.9, 0.9); this._winTo = new THREE.Vector2(-0.9, 0.9); this._winAt = -1e9;

    // The vortices: positions in aspect units, alternate signs, slow drift.
    this._vortices = [];
    for (let i = 0; i < VORTEX_MAX; i++) {
      const v = new THREE.Vector4(0.1 + Math.random() * (aspect - 0.2), 0.1 + Math.random() * 0.8, 0, 0.07 + Math.random() * 0.09);
      v.z = (i % 2 === 0 ? 1 : -1) * (0.14 + Math.random() * 0.1);
      this._vortices.push(v);
    }
    this._vortBase = this._vortices.map((v) => v.z);
    this._vortDrift = this._vortices.map(() => ({ x: (Math.random() - 0.5) * VORTEX_DRIFT * 2, y: (Math.random() - 0.5) * VORTEX_DRIFT * 2 }));
    this._boostIdx = 0; this._boostAt = -1e9;
    this._dropsInj = []; for (let i = 0; i < DROP_MAX; i++) this._dropsInj.push(new THREE.Vector4(0, 0, 0, -1));
    this._dropNext = 0;
    this._spot = new THREE.Vector4(0, 0, 0, 0); this._spotAt = -1e9; this._fill = new THREE.Vector3(1, 0, 0);
    this._addPending = 0; this._releasePending = 0; this._fragUntil = -1e9; this._massAt = -1e9;

    this._buffer = new FeedbackBuffer({ width: W, height: H, type: THREE.FloatType, filter: THREE.NearestFilter });
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;

    // --- the simulation pass
    const simMat = materiau('sim', () => new THREE.ShaderMaterial({
      uniforms: {
        uPrev: { value: null }, uRes: { value: new THREE.Vector2(1, 1) }, uAspect: { value: 1 }, uDt: { value: FIXED_DT }, uTime: { value: 0 },
        uCubic: { value: 0 }, uVort: { value: null }, uNVort: { value: 0 }, uVortGain: { value: 1 }, uCurl: { value: 0 }, uConf: { value: 0 },
        uGravity: { value: 0 }, uDrops: { value: null }, uDropR: { value: 0.05 }, uSpot: { value: null }, uFill: { value: null },
        uAddStep: { value: 0 }, uReset: { value: 1 }, uSeed: { value: 0 }
      },
      vertexShader: fullscreenVertexShader, fragmentShader: simFragmentShader, depthTest: false, depthWrite: false
    }));
    const su = this._simU = simMat.uniforms;
    su.uPrev.value = null; su.uRes.value.copy(this._res); su.uAspect.value = aspect; su.uDt.value = FIXED_DT; su.uTime.value = 0;
    su.uCubic.value = this._layout.cubic ? 1 : 0; su.uVort.value = this._vortices; su.uNVort.value = this._layout.vortices; su.uVortGain.value = 1;
    su.uCurl.value = 0.02; su.uConf.value = 2.0; su.uGravity.value = 0.008; su.uDrops.value = this._dropsInj; su.uDropR.value = 0.06;
    su.uSpot.value = this._spot; su.uFill.value = this._fill; su.uAddStep.value = 0; su.uReset.value = 1; su.uSeed.value = Math.random() * 64;
    this._simScene = new THREE.Scene();
    this._simQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), simMat);
    this._simQuad.frustumCulled = false;
    this._simScene.add(this._simQuad);
    this._geometries.push(this._simQuad.geometry);

    // --- the readback probe (tests), allocated lazily
    this._probeRT = null;

    // --- the display quad in the main scene
    const dispMat = materiau('display', () => new THREE.ShaderMaterial({
      uniforms: {
        uH: { value: null }, uLut: { value: lutTexture() }, uRes: { value: new THREE.Vector2(1, 1) }, uAspect: { value: 1 },
        uShowH: { value: 0 }, uCubic: { value: 0 }, uTime: { value: 0 }, uSeed: { value: 0 }, uWinPos: { value: new THREE.Vector2(-0.9, 0.9) }, uWinTint: { value: new THREE.Vector3(1, 1, 1) },
        uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
      },
      vertexShader: fullscreenVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false
    }));
    const du = this._dispU = dispMat.uniforms;
    du.uH.value = null; du.uRes.value.copy(this._res); du.uAspect.value = aspect; du.uShowH.value = this._showH ? 1 : 0; du.uCubic.value = this._layout.cubic ? 1 : 0;
    du.uTime.value = 0; du.uSeed.value = 0; du.uWinPos.value.set(-0.9, 0.9); du.uWinTint.value.set(1, 1, 1);
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), dispMat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    this._objects.push(this._display); this._geometries.push(this._display.geometry);
    scene.add(this._display);

    this._applyPalette(null);
  }

  _applyPalette(profile) {
    const palette = profile && profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const src = vivid.length ? vivid : dominant;
    _col.set(src.length ? src[0] : '#ffffff');
    _col.getHSL(_hsl);
    _col.setHSL(_hsl.h, Math.min(0.8, _hsl.s), 0.6);
    // 30% of the palette on the window's reflection, nothing on the film.
    const tint = new THREE.Color(1, 1, 1).lerp(_col, 0.3);
    this._dispU.uWinTint.value.set(tint.r, tint.g, tint.b);
  }

  onTrackChange(profile) { this._applyPalette(profile); }

  // Seconds until the next strong drop, from the analysis marks (the pointer
  // of shatter.js / panels.js: fires on the first frame at or after the mark).
  _updateDropSchedule(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks;
      this._drops = [];
      if (marks) for (let i = 0; i < marks.length; i++) if (marks[i].type === 'drop' && marks[i].score >= DROP_SCORE_MIN) this._drops.push(marks[i].atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return null;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    const ref = this._lastNowMs != null ? Math.min(this._lastNowMs, now) : now;
    this._lastNowMs = now;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] < ref - DROP_STALE_MS) this._dropIdx++;
    if (this._dropIdx >= this._drops.length) return null;
    return (this._drops[this._dropIdx] - now) / 1000;
  }

  // --- the events
  _injectDrop() {
    const d = this._dropsInj[this._dropNext];
    this._dropNext = (this._dropNext + 1) % DROP_MAX;
    // Another thickness than the rest: either side of the vivid range.
    const h = Math.random() < 0.5 ? 110 + Math.random() * 110 : 400 + Math.random() * 90;
    d.set(0.08 + Math.random() * (this._aspect - 0.16), 0.1 + Math.random() * 0.8, h, 0);
  }
  _startSpot() {
    this._spot.set(0.15 + Math.random() * (this._aspect - 0.3), 0.15 + Math.random() * 0.7, 0, 1);
    this._spotAt = this._time;
    const a = Math.random() * Math.PI * 2;
    this._fill.set(Math.cos(a), Math.sin(a), 0);
  }
  _massInjection() {
    this._addPending = DROP_ADD_NM;
    this._releasePending = DROP_ADD_NM;
    this._massAt = this._time;
    this._fragUntil = this._time + DROP_FRAG_BEATS * this._beatS;
  }

  // ---------------------------------------------------------------- update --
  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    if (paused) return;                       // gel
    const director = profile.director || {};
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    const t = this._time;

    if (profile.artworkPalette && profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._applyPalette(profile); }

    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 200) : 0;
    this._beatS = bpm ? 60 / bpm : FREE_BEAT_S;
    const beatS = this._beatS;
    const measured = !!director.hasReliableGrid;
    const bands = director.bands || {};
    const low = measured ? (bands.low || 0) : 0.5, mid = measured ? (bands.mid || 0) : 0.5, high = measured ? (bands.high || 0) : 0.5;
    const k = Math.min(1, dt * 6);
    this._low += (low - this._low) * k; this._mid += (mid - this._mid) * k; this._high += (high - this._high) * k;

    // --- clocks
    let beatEdge = false, barEdge = false, phraseEdge = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
      this._lastBeatPhase = profile.beatPhase;
    } else if (t - this._freeBeatAt >= beatS) { this._freeBeatAt = t; beatEdge = true; }
    if (beatEdge) this._beatCount++;
    if (profile.barPhase != null) {
      if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase) barEdge = true;
      this._lastBarPhase = profile.barPhase;
    } else if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
    if (beatEdge) this._beatsSincePhrase++;
    if (!phraseEdge && beatEdge && this._beatsSincePhrase >= 16) phraseEdge = true;
    if (phraseEdge) this._beatsSincePhrase = 0;
    this._lastPhrase = phrase;

    // --- the beat: a drop of another thickness, sized by the lows
    if (beatEdge) this._injectDrop();
    for (const d of this._dropsInj) if (d.w >= 0) { d.w += dt; if (d.w > 2) d.w = -1; }
    this._simU.uDropR.value = 0.045 + 0.09 * this._low;
    // --- the bar: one vortex spins faster for a beat
    if (barEdge) { this._boostIdx = Math.floor(Math.random() * this._layout.vortices); this._boostAt = t; }
    // --- the phrase: a black spot, then the flow that fills it
    if (phraseEdge && this._spot.w === 0) this._startSpot();
    if (this._spot.w > 0) {
      const age = (t - this._spotAt) / beatS;
      if (age < SPOT_GROW_BEATS) {
        this._spot.z = SPOT_RADIUS * smoother(age / SPOT_GROW_BEATS); this._spot.w = 1;
      } else if (age < SPOT_GROW_BEATS + SPOT_FILL_BEATS) {
        this._spot.z = SPOT_RADIUS; this._spot.w = 2; this._fill.z = (age - SPOT_GROW_BEATS) / SPOT_FILL_BEATS;
      } else {
        this._spot.w = 0;
      }
    }
    // --- the drop: the whole film thickens in a beat, then fragments
    const sUntil = this._updateDropSchedule(profile);
    if (sUntil != null && sUntil <= 0 && t - this._massAt > 2) this._massInjection();
    if (this._debugBurst && t >= this._debugBurstAt) { this._massInjection(); this._debugBurstAt = t + 12; }
    // --- the window slides a notch on the bar
    if (barEdge) {
      this._winStep = (this._winStep + 1) % 4;
      this._winFrom.copy(this._dispU.uWinPos.value);
      this._winTo.set(-0.9 + 0.3 * this._winStep, 0.9 - 0.15 * (this._winStep % 2));
      this._winAt = t;
    }
    this._dispU.uWinPos.value.lerpVectors(this._winFrom, this._winTo, smoother((t - this._winAt) / 0.5));

    // --- the vortices drift, bounce, and one is boosted
    const frag = t < this._fragUntil ? 1 : 0;
    for (let i = 0; i < this._layout.vortices; i++) {
      const v = this._vortices[i], dr = this._vortDrift[i];
      dr.x += (Math.random() - 0.5) * 0.02 * dt; dr.y += (Math.random() - 0.5) * 0.02 * dt;
      const sp = Math.hypot(dr.x, dr.y); if (sp > VORTEX_DRIFT * 1.5) { dr.x *= VORTEX_DRIFT * 1.5 / sp; dr.y *= VORTEX_DRIFT * 1.5 / sp; }
      v.x += dr.x * dt; v.y += dr.y * dt;
      if (v.x < 0.05) { v.x = 0.05; dr.x = Math.abs(dr.x); } if (v.x > this._aspect - 0.05) { v.x = this._aspect - 0.05; dr.x = -Math.abs(dr.x); }
      if (v.y < 0.05) { v.y = 0.05; dr.y = Math.abs(dr.y); } if (v.y > 0.95) { v.y = 0.95; dr.y = -Math.abs(dr.y); }
      let g = this._vortBase[i] * (1 + 1.0 * frag);
      if (i === this._boostIdx) g *= 1 + (BAR_BOOST - 1) * Math.max(0, 1 - (t - this._boostAt) / beatS);
      v.z = g;
    }
    const su = this._simU;
    su.uVortGain.value = 0.6 + 0.9 * this._mid;
    su.uCurl.value = (0.012 + 0.03 * this._high) * (1 + frag);
    su.uConf.value = (1.5 + 3.0 * this._high) * (1 + 1.5 * frag);
    su.uTime.value = t % 1000;
    this._dispU.uTime.value = t % 1000;
    this._dispU.uSeed.value = Math.floor(t * 60) % 61;
    this._accumulator += dt;
  }

  // The thickness field sampled at PROBE_W x PROBE_H, read back from the GPU
  // (the acceptance probe).
  sondeEpaisseur(renderer) {
    if (!this._probeRT) {
      this._probeRT = new THREE.WebGLRenderTarget(PROBE_W, PROBE_H, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, type: THREE.FloatType, depthBuffer: false, stencilBuffer: false });
      const mat = materiau('probe', () => new THREE.ShaderMaterial({ uniforms: { uH: { value: null } }, vertexShader: fullscreenVertexShader, fragmentShader: probeFragmentShader, depthTest: false, depthWrite: false }));
      this._probeU = mat.uniforms;
      this._probeScene = new THREE.Scene();
      const q = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat); q.frustumCulled = false;
      this._probeScene.add(q); this._geometries.push(q.geometry);
      this._probePixels = new Float32Array(PROBE_W * PROBE_H * 4);
    }
    this._probeU.uH.value = this._buffer.read.texture;
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(this._probeRT);
    renderer.render(this._probeScene, this._ortho);
    renderer.readRenderTargetPixels(this._probeRT, 0, 0, PROBE_W, PROBE_H, this._probePixels);
    renderer.setRenderTarget(prev);
    return this._probePixels;
  }

  _runPasses(renderer) {
    const rebuilt = this._buffer.ensureSize(renderer);
    if (rebuilt) this._simU.uReset.value = 1;
    if (this._paused) { this._dispU.uH.value = this._buffer.read.texture; return; }
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    let steps = 0;
    while (this._accumulator >= FIXED_DT && steps < MAX_SUBSTEPS) {
      // The DROP's thickening, spread over one beat of steps.
      let add = 0;
      if (this._addPending > 0) { add = Math.min(this._addPending, DROP_ADD_NM * FIXED_DT / this._beatS); this._addPending -= add; }
      else if (this._releasePending > 0) { const r = Math.min(this._releasePending, DROP_ADD_NM * FIXED_DT / (DROP_RELEASE_BEATS * this._beatS)); this._releasePending -= r; add = -r; }
      this._simU.uAddStep.value = add;
      this._simU.uPrev.value = this._buffer.read.texture;
      renderer.setRenderTarget(this._buffer.write);
      renderer.render(this._simScene, this._ortho);
      this._buffer.swap();
      this._simU.uReset.value = 0;
      this._accumulator -= FIXED_DT;
      steps++;
    }
    if (this._accumulator > FIXED_DT * MAX_SUBSTEPS) this._accumulator = FIXED_DT * MAX_SUBSTEPS;
    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
    this._dispU.uH.value = this._buffer.read.texture;
  }

  dispose() {
    for (const o of this._objects) if (o.parent) o.parent.remove(o);
    for (const g of this._geometries) g.dispose();
    this._objects = []; this._geometries = [];
    if (this._buffer) { this._buffer.dispose(); this._buffer = null; }
    if (this._probeRT) { this._probeRT.dispose(); this._probeRT = null; }
    // The textures the cached materials point at are about to go.
    this._simU.uPrev.value = null; this._dispU.uH.value = null;
    if (this._probeU) this._probeU.uH.value = null;
    this._scene = null;
  }
}
